'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import Link from 'next/link'
import { PublicCollectionChips } from './public-collection-chips'
import { PromptCardGrid } from './prompt-card-grid'
import { EmptyState } from './empty-states'
import { getPublicPromptsForUser } from '../_queries/prompts-queries'
import type { Collection, PromptsGridResult } from '@/types/collection.types'

interface PromptsPublicViewProps {
  targetUserId: string
  username: string
  displayName: string | null
  avatarUrl: string | null
  publicCollections?: Collection[]
  initialGrid?: PromptsGridResult
  /** When true, skips the outer wrapper and page header (embedded in profile page). */
  embedded?: boolean
  /** When true, shows "Manage in Vault" CTA and owner-specific empty states. */
  isOwnProfile?: boolean
}

/**
 * Public view - 2-column layout:
 * Top: Collection chips (horizontal scroll)
 * Below: Prompts grid (responsive 2–3 cols)
 */
export function PromptsPublicView({
  targetUserId,
  username,
  displayName,
  avatarUrl,
  publicCollections,
  initialGrid,
  embedded,
  isOwnProfile,
}: PromptsPublicViewProps) {
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null)

  const queryKey = ['public-prompts', targetUserId, activeCollectionId ?? 'all']

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      getPublicPromptsForUser({
        targetUserId,
        collectionId: activeCollectionId ?? undefined,
        limit: 20,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 60 * 1000,
    ...(activeCollectionId === null && initialGrid && initialGrid.prompts.length > 0
      ? {
          initialData: {
            pages: [initialGrid],
            pageParams: [undefined],
          },
        }
      : {}),
  })

  const allPrompts = data?.pages.flatMap((p) => p.prompts) ?? []
  const isEmpty = !isLoading && allPrompts.length === 0

  // Infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const name = displayName ?? username

  const inner = (
    <>
      {/* Page header */}
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
            {name}&apos;s Prompts
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            Public prompts and collections
          </p>
        </div>
      )}

      {/* Collection chips */}
      {publicCollections && publicCollections.length > 0 && (
        <div className="mb-6">
          <PublicCollectionChips
            collections={publicCollections}
            activeCollectionId={activeCollectionId}
            onSelect={setActiveCollectionId}
          />
        </div>
      )}

      {/* Manage in Vault banner (owner only, embedded) */}
      {isOwnProfile && embedded && (
        <Link
          href="/vault"
          className="mb-6 flex items-center justify-between rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-4 py-3 transition-colors hover:bg-[var(--color-bg-surface)]"
        >
          <span className="text-sm text-[var(--color-text-secondary)]">
            This is how visitors see your prompts.
          </span>
          <span className="shrink-0 rounded-md bg-[var(--color-brand-indigo)] px-3 py-1 text-xs font-medium text-white">
            Manage in Vault
          </span>
        </Link>
      )}

      {/* Empty state */}
      {isEmpty && (
        <EmptyState variant={isOwnProfile ? 'no-public-prompts-owner' : 'no-public-prompts'} />
      )}

      {/* Prompts grid */}
      {!isEmpty && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {allPrompts.map((prompt) => (
            <PromptCardGrid
              key={prompt.id}
              prompt={prompt}
              isOwnView={false}
              userId={targetUserId}
            />
          ))}
        </div>
      )}

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className="flex justify-center py-6">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-brand-indigo)] border-t-transparent" />
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="h-1" />
    </>
  )

  if (embedded) return inner

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      {inner}
    </div>
  )
}
